import { Eye, EyeOff } from 'react-feather';
import { isFuture, isToday } from 'date-fns';
import { useTranslation } from 'react-i18next';

import UI from '../../../../common/UI';
import useLocale from '../../../../common/useLocale';

interface Coupon {
  start_date?: string | null; // UTC
  end_date?: string | null; // UTC
  active: boolean;
  available: boolean;
}

export interface CouponStatusIndicatorProps {
  coupon: Coupon;
}

const CouponStatusIndicator = ({ coupon }: CouponStatusIndicatorProps) => {
  const { status, message } = useCouponStatus(coupon);

  const icons = {
    [CouponStatus.Available]: <Eye />,
    [CouponStatus.Disabled]: <EyeOff />,
    [CouponStatus.UsedUp]: <EyeOff />,
    [CouponStatus.StartsOn]: <EyeOff />,
    [CouponStatus.EndsOn]: <Eye />,
    [CouponStatus.EndedOn]: <EyeOff />,
  };

  const icon = icons[status];

  return (
    <>
      <UI.Icon>
        {icon}
      </UI.Icon>
      {' '}
      {message}
    </>
  );
};

export enum CouponStatus {
  Available = 'available',
  Disabled = 'disabled',
  UsedUp = 'used_up',
  StartsOn = 'starts_on',
  EndsOn = 'ends_on',
  EndedOn = 'ended_on',
}

export const useCouponStatus = (coupon: Coupon) => {
  const { t } = useTranslation();
  const { parseDate, formatDate } = useLocale();

  const status = (status: CouponStatus, message: string, date: string = undefined) => ({
    status, message, date,
  });

  if (!coupon.active) {
    return status(CouponStatus.Disabled, t('disabled'));
  }

  if (!coupon.available) {
    return status(CouponStatus.UsedUp, t('used_up'));
  }

  if (coupon.start_date) {
    const startDate = parseDate(coupon.start_date, { timezone: 'UTC' });

    if (isFuture(startDate)) {
      const date = formatDate(
        startDate,
        { format: isToday(startDate) ? 'display_time' : 'display_compact_date' },
      );

      return status(CouponStatus.StartsOn, t('starts_on', { date }), date);
    }
  }

  if (coupon.end_date) {
    const endDate = parseDate(coupon.end_date, { timezone: 'UTC' });

    const date = formatDate(
      endDate,
      { format: isToday(endDate) ? 'display_time' : 'display_compact_date' },
    );

    if (isFuture(endDate)) {
      return status(CouponStatus.EndsOn, t('ends_on_date', { date }), date);
    }

    return status(CouponStatus.EndedOn, t('ended_on', { date }), date);
  }

  return status(CouponStatus.Available, t('available'));
};

export default CouponStatusIndicator;
